import { apiService } from './api';
import { ApiResponse, GeneratedImage } from '@/types';
import { 
  processImageFile, 
  getOptimalProcessingOptions, 
  validateImageFile,
  type ProcessedImageData 
} from '@/utils/imageProcessing';

class ImageService {
  async generateImage(imageFile: File | Blob, prompt?: string): Promise<ApiResponse<GeneratedImage>> {
    try {
      // Convert Blob to File if necessary
      const file = imageFile instanceof File 
        ? imageFile 
        : new File([imageFile], 'image.jpg', { type: imageFile.type });

      // Validate file first
      const validation = validateImageFile(file);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error || 'Invalid file'
        };
      }

      console.log(`Processing image: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);

      // Get optimal processing options based on file size
      const processingOptions = getOptimalProcessingOptions(file.size);
      console.log('Using processing options:', processingOptions);

      // Process image (resize/compress if needed) and convert to base64
      const processedImage: ProcessedImageData = await processImageFile(file, processingOptions);
      
      console.log(`Image processed: ${processedImage.fileName}`);
      console.log(`Original size: ${(processedImage.originalSize / 1024 / 1024).toFixed(2)}MB`);
      console.log(`Processed size: ${(processedImage.fileSize / 1024 / 1024).toFixed(2)}MB`);
      console.log(`Compressed: ${processedImage.compressed}`);
      
      // Send processed data to backend
      const requestData = {
        imageBase64: processedImage.base64,
        fileName: processedImage.fileName,
        fileType: processedImage.fileType,
        fileSize: processedImage.fileSize,
        prompt: prompt || 'Create a professional e-commerce product image'
      };

      return await apiService.post('/images/generate', requestData);
    } catch (error: any) {
      console.error('Image generation error:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to generate image'
      };
    }
  }

  async getUserImages(): Promise<ApiResponse<GeneratedImage[]>> {
    try {
      return await apiService.get('/images/my-images');
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch images'
      };
    }
  }

  async getImageById(imageId: string): Promise<ApiResponse<GeneratedImage>> {
    try {
      return await apiService.get(`/images/${imageId}`);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch image'
      };
    }
  }

  async deleteImage(imageId: string): Promise<ApiResponse<{ message: string }>> {
    try {
      return await apiService.delete(`/images/${imageId}`);
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete image'
      };
    }
  }
}

export const imageService = new ImageService();